import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

import { DomainResult, TestResult } from '../../types/index';
import TestResultDisplay from '../common/TestResultDisplay';

interface CognitionAssessmentDetailProps {
  assessmentData?: DomainResult;
}

// 获取当前时间信息的函数
const getCurrentTimeInfo = () => {
  const now = new Date();
  const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    date: now.getDate(),
    weekdayName: weekdays[now.getDay()]
  };
};

const CognitionAssessmentDetail: React.FC<CognitionAssessmentDetailProps> = ({ assessmentData }) => {
  const navigate = useNavigate();

  // 🚨 明显的调试标记 - 如果看到这个说明组件被正确加载了
  console.log('🚨🚨🚨 CognitionAssessmentDetail 组件已加载 - 2025-08-30 v2 🚨🚨🚨');
  console.log('🔥 传入的assessmentData:', assessmentData);

  if (!assessmentData) {
    return (
      <div className="p-4 text-center text-gray-600">
        <AlertCircle className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
        <h3 className="text-lg font-semibold">加载评估数据时出错...</h3>
        <p className="text-sm">未能获取到有效的评估数据。</p>
      </div>
    );
  }

  // 渲染筛查问题结果
  const renderFilterQuestionResult = () => {
    const filterPassed = assessmentData.filterQuestionResult;
    return (
      <div className={`p-4 rounded-lg border ${filterPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          筛查问题
        </h3>
        <div className="text-sm space-y-1">
          <p>题目：您有记忆或定向方面的问题吗？</p>
          <p>回答：<span className={`font-medium ${filterPassed ? 'text-green-700' : 'text-red-700'}`}>{filterPassed ? '没有困难' : '有困难'}</span></p>
          <p>结果：{filterPassed ? <span className="text-green-700 font-medium">✓ 通过</span> : <span className="text-red-700 font-medium">✗ 未通过</span>}</p>
        </div>
      </div>
    );
  };

  // 渲染时间定向测试结果
  const renderTimeOrientationResult = (testResult: TestResult) => {
    if (!testResult.rawUserAnswers) return null;
    const { answers, results } = testResult.rawUserAnswers;
    const timeInfo = getCurrentTimeInfo();
    const correctCount = Object.values(results).filter(r => r === true).length;

    return (
      <div className={`p-4 rounded-lg border ${testResult.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <h3 className="font-semibold text-gray-900 mb-2">时间定向测试 ({correctCount}/4)</h3>
        <div className="text-sm space-y-1">
          <p>年份：{answers.year} {results.year ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />} (正确：{timeInfo.year}年)</p>
          <p>月份：{answers.month} {results.month ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />} (正确：{timeInfo.month}月)</p>
          <p>日期：{answers.date} {results.date ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />} (正确：{timeInfo.date}日)</p>
          <p>星期：{answers.weekday} {results.weekday ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />} (正确：星期{timeInfo.weekdayName})</p>
        </div>
      </div>
    );
  };

  // 渲染空间定向测试结果
  const renderSpaceOrientationResult = (testResult: TestResult) => {
    if (!testResult.rawUserAnswers) return null;
    const { answers, validation } = testResult.rawUserAnswers;

    return (
      <div className={`p-4 rounded-lg border ${testResult.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <h3 className="font-semibold text-gray-900 mb-2">空间定向测试</h3>
        <div className="text-sm space-y-1">
          <p>城市：{answers.city} {validation.details.city ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />}</p>
          <p>地点：{answers.place} {validation.details.place ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />}</p>
          <p>区域：{answers.area} {validation.details.area ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />}</p>
          <p>交通方式：{answers.transport} {validation.details.transport ? <CheckCircle className="inline w-4 h-4 text-green-600" /> : <XCircle className="inline w-4 h-4 text-red-600" />}</p>
          {validation.details.logicalError ? (
            <p className="text-xs text-red-600 mt-2">
              <XCircle className="inline w-3 h-3 mr-1" /> 逻辑冲突：{validation.details.errorReason}
            </p>
          ) : (
            <p className="text-xs text-green-600 mt-2"><CheckCircle className="inline w-3 h-3 mr-1" /> 逻辑合理</p>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">*系统检查明显的逻辑冲突</p>
      </div>
    );
  };

  // 渲染词汇记忆测试结果
  const renderWordRecallResult = (testResult: TestResult) => {
    if (!testResult.rawUserAnswers?.answers) return null;
    const { answers } = testResult.rawUserAnswers;
    const requiredWords = ['花', '门', '米饭'];

    return (
      <div className={`p-4 rounded-lg border ${testResult.passed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <h3 className="font-semibold text-gray-900 mb-2">词汇记忆测试 ({answers.correctCount || 0}/{answers.totalWords || 3})</h3>
        <div className="text-sm space-y-1">
          <p>要求记忆：{requiredWords.join('、')}</p>
          <p>用户选择：{answers.selectedWords?.join('、') || '无'}</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {requiredWords.map(word => (
              <span
                key={word}
                className={`px-2 py-1 rounded text-xs font-medium ${
                  answers.selectedWords?.includes(word)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {word} {answers.selectedWords?.includes(word) ? <CheckCircle className="inline w-3 h-3" /> : <XCircle className="inline w-3 h-3" />}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // 构建详细测试结果数据，用于TestResultDisplay
  const buildDetailedTestResults = () => {
    const timeInfo = getCurrentTimeInfo();
    const timeTest = assessmentData.testResults?.find(r => r.name === "时间定向");
    const spaceTest = assessmentData.testResults?.find(r => r.name === "空间定向");
    const wordTest = assessmentData.testResults?.find(r => r.name === "三词记忆");
    
    // 详细调试信息
    console.log('=== DEBUG CognitionAssessmentDetail ===');
    console.log('assessmentData:', assessmentData);
    console.log('testResults:', assessmentData.testResults);
    console.log('timeTest:', timeTest);
    console.log('timeTest.rawUserAnswers:', timeTest?.rawUserAnswers);
    console.log('spaceTest:', spaceTest);
    console.log('spaceTest.rawUserAnswers:', spaceTest?.rawUserAnswers);
    console.log('wordTest:', wordTest);
    console.log('wordTest.rawUserAnswers:', wordTest?.rawUserAnswers);
    console.log('responses字段:', assessmentData.responses);
    console.log('==========================================');
    
    const testGroups = [];
    
    // 时间定向测试组
    if (timeTest) {
      console.log('🔍 时间定向测试详细数据结构检查:');
      console.log('timeTest:', timeTest);
      console.log('timeTest.rawUserAnswers:', timeTest.rawUserAnswers);
      
      if (timeTest.rawUserAnswers) {
        console.log('rawUserAnswers存在，开始解构...');
        const { answers, results } = timeTest.rawUserAnswers;
        console.log('解构后的answers:', answers);
        console.log('解构后的results:', results);
        console.log('answers.year:', answers?.year);
        console.log('answers.month:', answers?.month);
        console.log('answers.date:', answers?.date);
        console.log('answers.weekday:', answers?.weekday);
        
        const correctCount = Object.values(results || {}).filter(r => r === true).length;
        testGroups.push({
          title: '时间定向测试',
          score: { current: correctCount, total: 4 },
          passed: timeTest.passed,
          items: [
            {
              name: '年份',
              passed: results.year,
              userAnswer: answers.year || '',
              correctAnswer: `${timeInfo.year}年`
            },
            {
              name: '月份',
              passed: results.month,
              userAnswer: answers.month || '',
              correctAnswer: `${timeInfo.month}月`
            },
            {
              name: '日期',
              passed: results.date,
              userAnswer: answers.date || '',
              correctAnswer: `${timeInfo.date}日`
            },
            {
              name: '星期',
              passed: results.weekday,
              userAnswer: answers.weekday || '',
              correctAnswer: `星期${timeInfo.weekdayName}`
            }
          ]
        });
      } else {
        // 数据缺失时的提示显示
        testGroups.push({
          title: '时间定向测试',
          score: { current: 0, total: 4 },
          passed: timeTest.passed,
          dataUnavailable: true,
          items: [
            {
              name: '年份',
              passed: false,
              userAnswer: '数据不可用',
              correctAnswer: `${timeInfo.year}年`
            },
            {
              name: '月份',
              passed: false,
              userAnswer: '数据不可用',
              correctAnswer: `${timeInfo.month}月`
            },
            {
              name: '日期',
              passed: false,
              userAnswer: '数据不可用',
              correctAnswer: `${timeInfo.date}日`
            },
            {
              name: '星期',
              passed: false,
              userAnswer: '数据不可用',
              correctAnswer: `星期${timeInfo.weekdayName}`
            }
          ]
        });
      }
    }
    
    // 空间定向测试组
    if (spaceTest) {
      console.log('🔍 空间定向测试详细数据结构检查:');
      console.log('spaceTest:', spaceTest);
      console.log('spaceTest.rawUserAnswers:', spaceTest.rawUserAnswers);
      
      if (spaceTest.rawUserAnswers) {
        console.log('空间定向rawUserAnswers存在，开始解构...');
        const { answers, validation } = spaceTest.rawUserAnswers;
        console.log('解构后的空间定向answers:', answers);
        console.log('解构后的空间定向validation:', validation);
        testGroups.push({
          title: '空间定向测试',
          passed: spaceTest.passed,
          hasLogicalError: validation.details.logicalError,
          errorReason: validation.details.errorReason,
          note: validation.details.logicalError ? '*系统检查明显的逻辑冲突' : '*系统检查无明显的逻辑冲突',
          items: [
            {
              name: '城市',
              passed: validation.details.city,
              userAnswer: answers.city || ''
            },
            {
              name: '地点',
              passed: validation.details.place,
              userAnswer: answers.place || ''
            },
            {
              name: '区域',
              passed: validation.details.area,
              userAnswer: answers.area || ''
            },
            {
              name: '交通方式',
              passed: validation.details.transport,
              userAnswer: answers.transport || ''
            }
          ]
        });
      } else {
        // 数据缺失时的提示显示
        testGroups.push({
          title: '空间定向测试',
          passed: spaceTest.passed,
          dataUnavailable: true,
          note: '*用户输入数据不可用',
          items: [
            {
              name: '城市',
              passed: false,
              userAnswer: '数据不可用'
            },
            {
              name: '地点',
              passed: false,
              userAnswer: '数据不可用'
            },
            {
              name: '区域',
              passed: false,
              userAnswer: '数据不可用'
            },
            {
              name: '交通方式',
              passed: false,
              userAnswer: '数据不可用'
            }
          ]
        });
      }
    }
    
    // 词汇记忆测试组
    if (wordTest) {
      console.log('🔍 词汇记忆测试详细数据结构检查:');
      console.log('wordTest:', wordTest);
      console.log('wordTest.rawUserAnswers:', wordTest.rawUserAnswers);
      
      const requiredWords = ['花', '门', '米饭'];
      if (wordTest.rawUserAnswers?.answers) {
        console.log('词汇记忆rawUserAnswers.answers存在，开始解构...');
        const { answers } = wordTest.rawUserAnswers;
        console.log('解构后的词汇记忆answers:', answers);
        testGroups.push({
          title: '词汇记忆测试',
          score: { current: answers.correctCount || 0, total: 3 },
          passed: wordTest.passed,
          items: [
            {
              name: '要求记忆',
              passed: true,
              userAnswer: requiredWords.join('、')
            },
            ...requiredWords.map(word => ({
              name: word,
              passed: answers.selectedWords?.includes(word) || false,
              userAnswer: answers.selectedWords?.includes(word) ? '✓' : '✗'
            }))
          ]
        });
      } else {
        // 数据缺失时的提示显示
        testGroups.push({
          title: '词汇记忆测试',
          score: { current: 0, total: 3 },
          passed: wordTest.passed,
          dataUnavailable: true,
          items: [
            {
              name: '要求记忆',
              passed: true,
              userAnswer: requiredWords.join('、')
            },
            ...requiredWords.map(word => ({
              name: word,
              passed: false,
              userAnswer: '数据不可用'
            }))
          ]
        });
      }
    }
    
    return testGroups;
  };

  const renderAssessmentDetails = () => {
    // 如果筛查问题未通过，只显示筛查问题
    if (assessmentData.completionMethod === 'filter_question') {
      return (
        <div className="space-y-4">
          {renderFilterQuestionResult()}
        </div>
      );
    }

    // 显示筛查问题和详细测试结果
    const testGroups = buildDetailedTestResults();
    
    return (
      <div className="space-y-4">
        {renderFilterQuestionResult()}
        
        {/* 具体测试结果 */}
        <div className="bg-white rounded-lg p-1">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            具体测试结果
          </h3>
          <TestResultDisplay
            title=""
            subtitle=""
            testGroups={testGroups}
            className="-mt-6"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {renderAssessmentDetails()}
      </div>
    </div>
  );
};

export default CognitionAssessmentDetail;
